import { ListButton } from "@refinedev/antd";
import {
  Select,
  Spin,
  Switch,
  Typography,
  message,
  Card,
  Collapse,
  Modal,
  InputNumber,
} from "antd";
import { useNavigation, useTranslate } from "@refinedev/core";
import {
  AppstoreOutlined,
  CheckOutlined,
  ContactsOutlined,
  ReadOutlined,
  TeamOutlined,
  ArrowLeftOutlined,
  ApiOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { Flex, Button, theme } from "antd";
import { useContext, useEffect, useRef, useState } from "react";
import {
  ChannelAccessPermissions,
  ChannelAccessPermissionsRef,
} from "@/components/channel-access-permissions";
import { Trans } from "react-i18next";
import { SmartMenuContext } from "@/config/context/smartMenuContext";
import {
  axiosInstance,
  MARGIN_UNIT_OPTIONS,
  normalizeMarginUnit,
} from "@/utilities";
import {
  API_URL_PERMISSIONS,
  API_URL_STORE_LINKED_SELLER,
} from "@/services/XCONS_ENV";
import { useLocation } from "react-router-dom";
import { BoxCloseIcon, PresupuestoIcon } from "@/components";
import { opcionesDeCategorias } from "@/pages/stores/usersPermissions/categorias";
import { NIVEL } from "@/utilities/niveles-de-permiso";

const ADMIN_OPTIONS = [
  { label: NIVEL.admin, value: "admin" },
  { label: NIVEL.no_access, value: "no_access" },
];

const EDITOR_OPTIONS = [
  { label: NIVEL.editor, value: "editor" },
  { label: NIVEL.no_access, value: "no_access" },
];

const VIEWER_OPTIONS = [
  { label: NIVEL.viewer, value: "viewer" },
  { label: NIVEL.no_access, value: "no_access" },
];

const PARTICIPATION_OPTIONS = [
  { label: NIVEL.admin, value: "admin" },
  { label: NIVEL.editor, value: "editor" },
  { label: NIVEL.viewer, value: "viewer" },
  { label: NIVEL.no_access, value: "no_access" },
];

const PERMISSION_OPTIONS = [
  { label: NIVEL.admin, value: "admin" },
  { label: NIVEL.editor, value: "editor" },
  { label: NIVEL.viewer, value: "viewer" },
  { label: NIVEL.no_access, value: "no_access" },
];

const ALL_PERMISSION_OPTIONS = [
  { label: "Admin para todos", value: "admin" },
  { label: "Editor para todos", value: "editor" },
  { label: "Viewer para todos", value: "viewer" },
  { label: "Por participación", value: "by_relation" },
];

type PermissionItem = {
  code: string;
  value: string;
  description?: string;
  /** Las opciones que declara el API. `categorias` las trae por store (xcons-docs#445). */
  options?: string[];
};

type ConfigItem = {
  code: string;
  value: string;
  description?: string;
};

const { Option } = Select;
const { Title, Text, Link } = Typography;
const { Panel } = Collapse;

export const LinkedSellerUserPermissionsPanel = ({
  typeIdentifier = "store_seller",
}: {
  typeIdentifier?: string;
}) => {
  const [structureLoading, setStructureLoading] = useState<boolean>(false);
  const [permissions, setPermissions] = useState<PermissionItem[]>([]);
  const [configs, setConfigs] = useState<ConfigItem[]>([]);
  const [isStoreActive, setIsStoreActive] = useState<boolean>(true);
  const [storeName, setStoreName] = useState<string>("");
  const [selectedOptions, setSelectedOptions] = useState<string>("");
  const [groupOptions, setGroupOptions] = useState<any>({});

  const t = useTranslate();
  const { token } = theme.useToken();
  const { goBack } = useNavigation();

  const location = useLocation();
  const record = location.state?.record;

  const { smartMenuName } = useContext(SmartMenuContext);

  const storeIdentifier = encodeURIComponent(
    smartMenuName?.replace(/\"/g, "").toLowerCase(),
  );

  const handleBack = () => {
    goBack();
  };

  // Grupo "Conversations" (N2): su estado vive en el conversation-service, así que persiste aparte.
  const channelPermsRef = useRef<ChannelAccessPermissionsRef>(null);

  /**
   * Fallback para el par (store, seller) que todavía no tiene fila de permisos — que es el caso de
   * casi todos los vínculos, porque los crea el sync sin detalle. Cuando la fila no existe el
   * endpoint devuelve `{ permissions: [] }` y se usa esto.
   *
   * Los valores son los `storeSellerDefaultValue` del backend: si acá dijeran otra cosa, el panel
   * mostraría un permiso que el sistema no está aplicando. Faltaban `quote` y `contact_group`, los
   * dos permisos que el panel no tenía cómo mostrar.
   */
  const DEFAULT_PERMISSIONS: PermissionItem[] = [
    { code: "contact", value: "viewer" },
    { code: "order", value: "viewer" },
    { code: "order_payment", value: "viewer" },
    { code: "order_shipment", value: "viewer" },
    { code: "contact_group", value: "no_access" },
    { code: "quote", value: "viewer" },
  ];

  const DEFAULT_CONFIGS: ConfigItem[] = [
    { code: "contact", value: "viewer" },
    { code: "contact_conversation", value: "viewer" },
    { code: "contact_quote", value: "viewer" },
    { code: "quote_participation", value: "viewer" },
    { code: "quote_max_manual_discount", value: "30" },
    // 028 — un operador nuevo nace sin ver márgenes: 'none', no 'false'.
    { code: "quote_access_margins", value: "none" },
    { code: "quote_commission_discount_level", value: "no_access" },
  ];

  const getPermissions = async () => {
    setStructureLoading(true);
    try {
      const res = await axiosInstance.get(
        `${API_URL_PERMISSIONS}/store_seller/${record?.storeCode}/users/${record?.sellerCode}`,
      );

      const data = res.data;
      setIsStoreActive(getLinkedSeller()?.active ?? false);
      setPermissions(data.permissions?.length ? data.permissions : DEFAULT_PERMISSIONS);
      setConfigs(data.configs?.length ? data.configs : DEFAULT_CONFIGS);
      setStoreName(data.identifier ?? record?.storeCode ?? "");
    } catch (error) {
      console.error("Error al obtener permisos:", error);
      // Fallback to defaults on error (e.g. no permissions configured yet)
      setIsStoreActive(getLinkedSeller()?.active ?? false);
      setPermissions(DEFAULT_PERMISSIONS);
      setConfigs(DEFAULT_CONFIGS);
      setStoreName(record?.storeCode ?? "");
    } finally {
      setStructureLoading(false);
    }
  };

  useEffect(() => {
    if (!record) {
      console.error("User ID no encontrado en el registro.");
      handleBack();
      return;
    }
    getPermissions();
  }, []);

  useEffect(() => {
    if (!permissions) return;

    const initialGroupOptions: Record<string, string> = {};

    PERMISSION_GROUPS.forEach((group: any) => {
      if (group.mainPermissionCode) {
        // Para un code que vive en los dos lados manda el permiso: es el que el backend
        // aplica, así que es el que el panel tiene que mostrar. Si sólo existe el config
        // —vínculos guardados antes del fix— se cae a él y el próximo save los alinea.
        const source =
          group.mainIsConfig && !group.mainAlsoPermission ? configs : permissions;
        const found =
          source.find((p: any) => p.code === group.mainPermissionCode) ??
          (group.mainAlsoPermission
            ? configs.find((c: any) => c.code === group.mainPermissionCode)
            : undefined);

        initialGroupOptions[group.key] = found?.value ?? "admin";
      }
    });

    setGroupOptions(initialGroupOptions);
  }, [permissions, configs]);

  const getLinkedSeller = () =>
    Array.isArray(record?.storeLinkedSellers)
      ? record.storeLinkedSellers[0]
      : record?.storeLinkedSellers;

  const handleToggleStore = async (checked: boolean) => {
    setStructureLoading(true);
    try {
      const payload = {
        active: checked ? 1 : 0,
      };

      await axiosInstance.patch(
        `${API_URL_STORE_LINKED_SELLER}/${getLinkedSeller()?.storeSellerId}`,
        payload,
      );
      setIsStoreActive(checked);
      message.success(`Store ${checked ? "enabled" : "disabled"} successfully`);
    } catch (error: any) {
      console.error("Error updating store:", error);
      message.error("Failed to update store status");

      setIsStoreActive((prev) => !checked);
    } finally {
      setStructureLoading(false);
    }
  };

  const getPermission = (code: string) =>
    permissions.find((p) => p.code === code);

  const getConfig = (code: string) => configs.find((c) => c.code === code);

  const renderSelect = (item: any, isConfig = false, groupKey?: string) => {
    if (!item) return null;

    const selected = groupKey ? groupOptions[groupKey] : null;

    let options =
      selected === "admin"
        ? ADMIN_OPTIONS
        : selected === "editor"
          ? EDITOR_OPTIONS
          : selected === "viewer"
            ? VIEWER_OPTIONS
            : selected === "by_relation"
              ? PARTICIPATION_OPTIONS
              : PERMISSION_OPTIONS;

    return (
      <Select
        options={options}
        value={item.value}
        onChange={(value) => {
          if (isConfig) {
            setConfigs((prev) =>
              prev.map((c) => (c.code === item.code ? { ...c, value } : c)),
            );
          } else {
            setPermissions((prev) =>
              prev.map((p) => (p.code === item.code ? { ...p, value } : p)),
            );
          }
        }}
        style={{ minWidth: 180 }}
      />
    );
  };

  const updateGroupValues = (
    permissionCodes: string[],
    configCodes: string[],
    value: string,
    mainPermissionCode?: string,
    mainIsConfig?: boolean,
    mainAlsoPermission?: boolean,
  ) => {
    const isByRelation = value === "by_relation" || value === "no_access";
    const appliedChildValue = isByRelation ? "no_access" : value;

    setPermissions((prev) => {
      let next = [...prev];

      // 1) Upsert del padre en `permissions`: siempre que no sea puramente config
      //    (`mainAlsoPermission` = el code existe como config Y como permiso).
      if (mainPermissionCode && (!mainIsConfig || mainAlsoPermission)) {
        const idx = next.findIndex((p) => p.code === mainPermissionCode);
        if (idx === -1) {
          next.push({ code: mainPermissionCode, value });
        } else {
          next[idx] = { ...next[idx], value };
        }
      }

      // 2) Actualizar hijos del grupo
      next = next.map((p) =>
        permissionCodes.includes(p.code)
          ? { ...p, value: appliedChildValue }
          : p,
      );

      return next;
    });

    const skipBulkConfigCodes = [
      "quote_max_manual_discount",
      "quote_access_margins",
      "quote_commission_discount_level",
    ];
    setConfigs((prev) => {
      let next = prev.map((c) =>
        configCodes.includes(c.code) && !skipBulkConfigCodes.includes(c.code)
          ? { ...c, value: appliedChildValue }
          : c,
      );

      // 1bis) Upsert del padre en `configs` si mainIsConfig
      if (mainPermissionCode && mainIsConfig) {
        const idx = next.findIndex((c) => c.code === mainPermissionCode);
        if (idx === -1) {
          next.push({ code: mainPermissionCode, value });
        } else {
          next[idx] = { ...next[idx], value };
        }
      }

      return next;
    });
  };

  const handleConfigChange = (code: string, value: string | number | null) => {
    setConfigs((prevConfigs: any) => {
      const exists = prevConfigs.some((c: any) => c.code === code);
      if (exists) {
        return prevConfigs.map((config: any) =>
          config.code === code ? { ...config, value } : config,
        );
      }
      return [...prevConfigs, { code, value }];
    });
  };

  const PERMISSION_GROUPS = [
    {
      key: "contacts",
      icon: <ContactsOutlined />,
      title: t("menu.label.contacts"),
      mainPermissionCode: "contact",
      /**
       * `contact` existe DOS veces para el par (store, seller): como config —con qué permiso se
       * crea la relación al tocar un contacto, que lee `participation-save.listener`— y como
       * permiso, que es el que `getAllPermissions` mergea sobre `store.contact[storeCode]` y con
       * el que el listado decide qué contactos mostrar.
       *
       * Este panel escribía sólo el config, así que el desplegable se guardaba, se veía, y el
       * acceso quedaba clavado en su default (`viewer`): lector de TODOS los contactos del store.
       * Visto en dev con un operador de seller que, con los tres desplegables en "Por relación",
       * seguía viendo los 14.247 contactos del store —y recién al abrir uno le saltaba el error
       * de permisos, porque el detalle sí lee el permiso—.
       *
       * Con `mainAlsoPermission` el mismo desplegable escribe los dos y ninguno queda mintiendo.
       * Es el mismo agujero que tuvo `quote` y que se tapó dándole su `mainPermissionCode`.
       */
      mainIsConfig: true,
      mainAlsoPermission: true,
      subtitle: t("label.permissionsofseller.instruction.contact"),
      permissionCodes: [],
      configCodes: ["contact_conversation", "contact_quote"],
    },
    {
      key: "contacts_groups",
      icon: <TeamOutlined />,
      title: t("label.contactsgroups"),
      subtitle: t("label.permissionscontact.group"),
      permissionCodes: ["contact_group"],
      configCodes: [],
      noButton: true,
    },
    {
      key: "quotes",
      icon: <PresupuestoIcon />,
      title: t("menu.label.quotes"),
      /**
       * El permiso `quote` del par (store, seller) — el que el store le da al seller sobre
       * Presupuestos — estaba comentado, y con él se caían dos cosas:
       *
       * 1. **No había con qué darlo.** Es el permiso que `getAllPermissions` mergea sobre
       *    `store.quote[storeCode]` para todos los operadores de ese seller: sin control, el par
       *    quedaba clavado en el default (`viewer`) y el store no podía ni subirlo ni sacarlo.
       * 2. **El select del grupo no rehidrataba.** `initialGroupOptions` sólo se arma para los
       *    grupos que tienen `mainPermissionCode`, así que al volver a entrar el desplegable de
       *    Presupuestos aparecía vacío — que es lo que se lee como "no guardó".
       *
       * El panel de operadores del store (`stores/usersPermissions`) siempre lo tuvo; esto empareja
       * los dos.
       */
      mainPermissionCode: "quote",
      subtitle: t("label.permissionsofseller.instruction.quote"),
      permissionCodes: [],
      configCodes: [
        "quote_participation",
        "quote_max_manual_discount",
        "quote_access_margins",
        "quote_commission_discount_level",
      ],
    },
    {
      key: "orders",
      icon: <BoxCloseIcon />,
      title: t("menu.label.orders"),
      mainPermissionCode: "order",
      subtitle: t("label.permissionsofseller.instruction.order"),
      permissionCodes: ["order_payment", "order_shipment"],
      configCodes: [],
    },
    {
      /**
       * Categorías del store para todos los operadores de este seller (xcons-docs#445): el árbol
       * de ESTE store, con los mismos niveles que el panel de operadores del store. Qué niveles
       * existen lo manda el API por store: en uno que comparte el árbol de XCONS, sólo No y
       * Viewer. Sin dar nada arranca en No.
       */
      key: "categorias",
      icon: <AppstoreOutlined />,
      title: t("label.permissions.categorias"),
      permissionCodes: ["categorias"],
      configCodes: [],
      noButton: true,
    },
  ];

  // El par store-seller no tiene permisos individuales: `store_permission`, el único que había,
  // dejó de configurarse (xcons-docs#537) y acá además era inerte, nadie leía esa fila.
  const groupedPermissions = PERMISSION_GROUPS;

  const LABELS_MAP: Record<string, React.ReactNode> = {
    contact_conversation: (
      <Text>
        <Trans
          i18nKey="label.permissionscontact.conversation"
          components={{ bold: <strong /> }}
        />
      </Text>
    ),
    contact_quote: (
      <Text>
        <Trans
          i18nKey="label.permissionscontact.quote"
          components={{ bold: <strong /> }}
        />
      </Text>
    ),
    contact_group: (
      <Flex vertical>
        <Text strong>
          <TeamOutlined /> {t("label.contactsgroups")}
        </Text>
        <Flex>
          <Text style={{ fontSize: "12px", marginLeft: 16 }}>
            {t("label.permissionscontact.group")}
          </Text>
        </Flex>
      </Flex>
    ),
    quote_participation: <Text>{t("label.permissionsquotes")}</Text>,
    quote_max_manual_discount: <Text>{t("label.maximumdiscount")}</Text>,
    quote_access_margins: <Text>{t("label.accessmargins")}</Text>,
    quote_commission_discount_level: (
      <Text>{t("label.commissionlevel")}</Text>
    ),

    order_payment: <Text>{t("label.permissionspaymenttransactions")}</Text>,
    order_shipment: <Text>{t("Label.deliveries")}</Text>,
    categorias: (
      <Flex vertical>
        <Text strong>
          <AppstoreOutlined /> {t("label.permissions.categorias")}
        </Text>
        <Text style={{ fontSize: "12px", marginLeft: 16 }}>
          {t(
            "label.permissionsofseller.instruction.categorias",
            "Ver (Lector), crear y editar (Editor) o además borrar (Admin) el árbol de categorías de este store.",
          )}
        </Text>
      </Flex>
    ),
  };

  const getPermissionLabel = (code: string) =>
    LABELS_MAP[code] ?? `Permission: ${code}`;

  const getConfigLabel = (code: string) =>
    LABELS_MAP[code] ?? `Config: ${code}`;

  const handleRemoveGroup = (
    permissionCodes: string[],
    configCodes: string[],
  ) => {
    updateGroupValues(permissionCodes, configCodes, "no_access");

    if (configCodes.includes("quote_max_manual_discount")) {
      handleConfigChange("quote_max_manual_discount", 0);
    }
    if (configCodes.includes("quote_access_margins")) {
      // 028 — el config es la unidad del margen: "sin acceso" es 'none', no 'false'.
      handleConfigChange("quote_access_margins", "none");
    }
    if (configCodes.includes("quote_commission_discount_level")) {
      handleConfigChange("quote_commission_discount_level", "no_access");
    }
  };

  // ---------------------------------------------------------------------------
  // Orders group: switch-based sub-permission UX (see Permissions Spec).
  // Mirrors the rules of the store-operator panel — the only difference is
  // the subject (the whole team of this linked seller).
  // ---------------------------------------------------------------------------
  const isOrdersSubOn = (value: string | undefined): boolean =>
    !!value && value !== "no_access";

  const isOrdersSwitchDisabled = (mainValue: string | undefined): boolean =>
    mainValue === "admin" || mainValue === "viewer";

  const handleOrderSubToggle = (
    code: "order_payment" | "order_shipment",
    mainValue: string | undefined,
    checked: boolean,
  ) => {
    const onValue =
      mainValue === "admin"
        ? "admin"
        : mainValue === "editor"
          ? "editor"
          : "editor";
    const next = checked ? onValue : "no_access";
    setPermissions((prev) =>
      prev.map((p) => (p.code === code ? { ...p, value: next } : p)),
    );
  };

  const normalizeOrdersForSave = (
    list: PermissionItem[],
  ): PermissionItem[] => {
    const main = list.find((p) => p.code === "order")?.value;
    if (!main) return list;
    return list.map((p) => {
      if (p.code !== "order_payment" && p.code !== "order_shipment") return p;
      if (main === "admin") return { ...p, value: "admin" };
      if (main === "viewer") return { ...p, value: "no_access" };
      // editor & by_relation: preserve each sub-switch as toggled by the admin
      // (the per-switch value defines what participating operators receive).
      if (main === "editor" || main === "by_relation") {
        return { ...p, value: p.value === "no_access" ? "no_access" : "editor" };
      }
      return p;
    });
  };

  const handleRemoveOrderParticipations = async () => {
    const sellerCode = record?.sellerCode;
    const storeCode = record?.storeCode;
    if (!sellerCode || !storeCode) return;
    try {
      const { data } = await axiosInstance.delete(
        `${API_URL_PERMISSIONS}/participation/seller/${encodeURIComponent(
          sellerCode,
        )}/store/${encodeURIComponent(storeCode)}?permissionCode=order`,
      );
      const deleted = data?.deleted ?? 0;
      message.success(
        t(
          "linkedseller.message.orderParticipations.removed",
          "Participaciones eliminadas",
        ) + ` (${deleted})`,
      );
    } catch (error: any) {
      const apiMsg =
        error?.response?.data?.message || error?.response?.data?.error;
      message.error(
        Array.isArray(apiMsg)
          ? apiMsg.join(", ")
          : apiMsg ||
              t(
                "linkedseller.message.orderParticipations.removeError",
                "Error al remover participaciones",
              ),
      );
    }
  };

  const confirmRemoveOrderParticipations = () => {
    Modal.confirm({
      title: t(
        "linkedseller.message.title.removeOrderParticipations",
        "¿Remover las participaciones del equipo del seller en pedidos?",
      ),
      content: t(
        "linkedseller.message.title.removeOrderParticipations.two",
        "Esta acción elimina las participaciones existentes en pedidos de este store para todos los operadores del seller. El nivel de permiso configurado se mantiene; tracking re-puede añadir participantes en pedidos nuevos.",
      ),
      onOk: () => {
        handleRemoveOrderParticipations();
      },
      width: 560,
      okText: t(
        "linkedseller.message.label.confirmRemoveOrderParticipations",
        "Remover participaciones",
      ),
      okType: "danger",
      okButtonProps: { type: "primary" },
      icon: <ExclamationCircleOutlined />,
      cancelButtonProps: { icon: <ArrowLeftOutlined /> },
      cancelText: t("buttons.back"),
      onCancel() {},
    });
  };

  const handleSavePermissions = async () => {
    setStructureLoading(true);
    try {
      const normalizedPermissions = normalizeOrdersForSave(permissions);
      const payload = {
        type: typeIdentifier,
        identifier: record?.storeCode,
        userId: record?.sellerCode,
        permissions: normalizedPermissions.map((p) => ({
          code: p.code,
          value: p.value,
        })),
        configs: configs.map((c) => ({
          code: c.code,
          value: c.value,
        })),
      };

      await axiosInstance.post(`${API_URL_PERMISSIONS}`, payload);

      // Los permisos de canal viven en otra base (conversation-service): se guardan con el mismo
      // botón, pero si fallan no se pierde lo que sí se guardó.
      try {
        await channelPermsRef.current?.persist();
      } catch (e: any) {
        message.warning(
          `Los permisos se guardaron, pero no los de canales: ${
            e?.response?.data?.message ?? e?.message ?? e
          }`,
        );
      }

      message.success("Permissions saved successfully");
    } catch (error: any) {
      // Sin esto un guardado rechazado no dejaba rastro en pantalla: no salía el "saved" ni ningún
      // error, y el operador se iba creyendo que había guardado. Un "no guarda" sin mensaje es
      // indistinguible de un "guardó y no se ve".
      console.error("Error al guardar permisos:", error);
      const apiMsg =
        error?.response?.data?.message ?? error?.message ?? "Failed to save permissions";
      message.error(Array.isArray(apiMsg) ? apiMsg.join(", ") : apiMsg);
    } finally {
      setStructureLoading(false);
    }
  };

  const handleDelete = async () => {
    setStructureLoading(true);
    try {
      await axiosInstance.delete(
        `${API_URL_STORE_LINKED_SELLER}/${getLinkedSeller()?.storeSellerId}`,
      );

      message.success("Unlinked successfully");
      handleBack();
    } catch (error) {
      console.error("Error al obtener permisos:", error);
    } finally {
      setStructureLoading(false);
    }
  };

  const warningDelete = () => {
    Modal.confirm({
      title: t("label.permissionsofseller.modal.unlink.title"),
      content: t("label.permissionsofseller.modal.unlink.content"),
      onOk: () => {
        handleDelete();
      },
      width: 490,
      okText: t("storeoperator.message.label.confirmremove"),
      okType: "danger",
      okButtonProps: {
        type: "primary",
      },
      icon: <ExclamationCircleOutlined />,
      cancelButtonProps: {
        icon: <ArrowLeftOutlined />,
      },
      cancelText: t("buttons.back"),
      onCancel() { },
    });
  };

  return (
    <Spin spinning={structureLoading}>
      <Flex vertical>
        <Flex align="center" justify="space-between">
          <Flex align="center">
            <ListButton
              onClick={handleBack}
              hideText
              icon={<ArrowLeftOutlined />}
            />
            <Title style={{ marginBottom: 0, marginLeft: "10px" }} level={3}>
              {t("label.permissionsofseller.title")} {record?.name}
            </Title>
          </Flex>

          <Flex align="center" gap={6}>
            <Text style={{ color: token.colorTextSecondary }}>FAQ</Text>
            <ReadOutlined style={{ color: token.colorTextSecondary }} />
          </Flex>
        </Flex>

        <Collapse defaultActiveKey={["limits"]} style={{ marginTop: 24 }}>
          <Panel
            className="permissions-collapse"
            key="limits"
            header={
              <Flex vertical>
                <Text strong>
                  {t("label.permissionsofseller.sellerlimits")}
                </Text>
                <Text style={{ fontSize: 12 }}>
                  {t("label.permissionsofseller.instruction.sellerlimits")}
                </Text>
              </Flex>
            }
          >
            <Flex vertical gap={24} style={{ marginLeft: "12%" }}>
              <Flex gap={16}>
                <Text>{t("label.permissionsofseller.status")}</Text>
                <Flex vertical gap={8}>
                  <Flex align="center" gap={8}>
                    <Text style={{ color: token.colorTextSecondary }}>
                      {t("label.permissionsofseller.swichdisabled")}
                    </Text>
                    <Switch
                      checked={isStoreActive}
                      onChange={handleToggleStore}
                      className={isStoreActive ? "active-switch" : ""}
                    />
                    <Text style={{ color: token.colorTextSecondary }}>
                      {isStoreActive
                        ? t("filter.products.labelStatusActive")
                        : t("filter.products.labelStatusDesactive")}
                    </Text>
                  </Flex>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t(
                      "label.permissionsofseller.instruction.sellerstatus.part1",
                    )}
                    <br />
                    {t(
                      "label.permissionsofseller.instruction.sellerstatus.part2",
                    )}
                  </Text>
                </Flex>
              </Flex>

              {/* <Flex gap={16}>
                <Text>Limit Seller service area: </Text>
                <Flex vertical gap={8}>
                  <Flex align="center" gap={8}>
                    <TextArea />
                  </Flex>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Limit the Seller geographic service area for the Store{" "}
                    <br />
                    within the specified <Link>GeoJSON.</Link> Without limits
                    when empty.
                  </Text>
                </Flex>
              </Flex> */}
            </Flex>
          </Panel>
        </Collapse>

        <Flex vertical gap={24}>
          <Collapse defaultActiveKey={["permissions"]}>
            <Panel
              className="permissions-collapse"
              key="permissions"
              header={
                <Flex vertical>
                  <Text strong>
                    {t("label.permissionsofseller.sellermanagement")}
                  </Text>
                  <Text style={{ fontSize: 12 }}>
                    {t(
                      "label.permissionsofseller.instruction.sellermanagement",
                    )}
                  </Text>
                </Flex>
              }
            >
              {groupedPermissions.map((group, index) => (
                <>
                  {/* Conversations va entre Contacts y Quotes (Figma). El nivel es del seller como
                      entidad: sus operadores lo heredan read-only. */}
                  {group.key === "quotes" && (
                    <ChannelAccessPermissions
                      ref={channelPermsRef}
                      storeCode={record?.storeCode ?? ""}
                      subject={{ kind: "seller", sellerCode: record?.sellerCode ?? "" }}
                    />
                  )}
                  <Card
                    key={`${group.key}-${index}`}
                    className={`permissions-card-section ${group.key === "contacts_groups"
                      ? "contacts-groups-card"
                      : ""
                      }`}
                    title={
                      !group.noButton &&
                      (group.permissionCodes.length > 1 ||
                        group.configCodes.length > 1) && (
                        <Flex justify="space-between" align="center">
                          <Flex vertical>
                            <Text
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              {group.icon} {group.title}
                            </Text>
                            <Text style={{ fontSize: "12px", marginLeft: 19 }}>
                              {group.subtitle}
                            </Text>
                          </Flex>

                          <Select
                            options={ALL_PERMISSION_OPTIONS}
                            placeholder="Seleccionar"
                            value={groupOptions[group.key]}
                            onChange={(value) => {
                              updateGroupValues(
                                group.permissionCodes,
                                group.configCodes,
                                value,
                                group.mainPermissionCode,
                                (group as any).mainIsConfig,
                                (group as any).mainAlsoPermission,
                              );
                              setGroupOptions((prev: any) => ({
                                ...prev,
                                [group.key]: value,
                              }));
                            }}
                            style={{ minWidth: 180 }}
                          />
                        </Flex>
                      )
                    }
                  >
                    {/* Renderizar configs */}
                    {group.configCodes.map((code, index) => {
                      const configItem = getConfig(code);
                      const isDiscountConfig =
                        code === "quote_max_manual_discount";
                      const isMarginToggle = code === "quote_access_margins";
                      const isCommissionLevel =
                        code === "quote_commission_discount_level";
                      const discountValue = Number(configItem?.value ?? 0);

                      return (
                        <Flex
                          key={`${group.key}-${code}-${index}`}
                          justify="space-between"
                          align="center"
                          style={{
                            marginBottom: 18,
                          }}
                        >
                          <Text>{getConfigLabel(code)}</Text>

                          {isDiscountConfig ? (
                            <InputNumber
                              style={{
                                minWidth: 180,
                                textAlignLast: "center",
                              }}
                              min={0}
                              max={100}
                              value={discountValue}
                              placeholder="-"
                              onChange={(value) => {
                                handleConfigChange(code, value);
                              }}
                            />
                          ) : isMarginToggle ? (
                            // 028 — la UNIDAD del margen, no un toggle. El valor se
                            // normaliza al renderizar porque las filas legacy siguen
                            // en 'true'/'false' y nunca se migran: sin eso, un store
                            // que hoy ve márgenes abriría el panel en "No access" y,
                            // al guardar, se sacaría el permiso a sí mismo.
                            <Select
                              style={{ minWidth: 180 }}
                              value={normalizeMarginUnit(getConfig(code)?.value)}
                              options={MARGIN_UNIT_OPTIONS}
                              onChange={(value) =>
                                handleConfigChange(code, value)
                              }
                            />
                          ) : isCommissionLevel ? (
                            <Select
                              style={{ minWidth: 180 }}
                              value={getConfig(code)?.value ?? "no_access"}
                              options={[
                                {
                                  label: t("label.commission.editor"),
                                  value: "editor",
                                },
                                {
                                  label: t("label.commission.viewer"),
                                  value: "viewer",
                                },
                                {
                                  label: t("label.commission.none"),
                                  value: "no_access",
                                },
                              ]}
                              onChange={(value) =>
                                handleConfigChange(code, value)
                              }
                            />
                          ) : (
                            renderSelect(configItem, true, group.key)
                          )}
                        </Flex>
                      );
                    })}

                    {/* Renderizar permisos */}
                    {group.permissionCodes.map((code) => {
                      const isOrdersSub =
                        group.key === "orders" &&
                        (code === "order_payment" ||
                          code === "order_shipment");
                      if (isOrdersSub) {
                        const mainValue = groupOptions["orders"];
                        const item = getPermission(code);
                        const disabled = isOrdersSwitchDisabled(mainValue);
                        const checked =
                          mainValue === "admin"
                            ? true
                            : mainValue === "viewer"
                              ? false
                              : isOrdersSubOn(item?.value);
                        return (
                          <Flex
                            key={`${group.key}-${code}`}
                            justify="space-between"
                            align="center"
                            style={{ marginBottom: 16 }}
                          >
                            <Text>{getPermissionLabel(code)}</Text>
                            <Switch
                              checked={checked}
                              disabled={disabled}
                              onChange={(c) =>
                                handleOrderSubToggle(
                                  code as
                                    | "order_payment"
                                    | "order_shipment",
                                  mainValue,
                                  c,
                                )
                              }
                            />
                          </Flex>
                        );
                      }
                      if (code === "categorias") {
                        // Sus propios niveles, no los del grupo: los decide el árbol del store.
                        const item = getPermission(code);
                        if (!item) return null;
                        return (
                          <Flex
                            key={`${group.key}-${code}`}
                            justify="space-between"
                            align="center"
                          >
                            {getPermissionLabel(code)}
                            <Select
                              options={opcionesDeCategorias(item)}
                              value={item.value || undefined}
                              placeholder="Seleccionar"
                              onChange={(value) =>
                                setPermissions((prev) =>
                                  prev.map((p) =>
                                    p.code === code ? { ...p, value } : p,
                                  ),
                                )
                              }
                              style={{ minWidth: 180 }}
                            />
                          </Flex>
                        );
                      }
                      return (
                        <Flex
                          key={`${group.key}-${code}`}
                          justify="space-between"
                          align="center"
                          style={group.noButton ? {} : { marginBottom: 16 }}
                        >
                          <Text>{getPermissionLabel(code)}</Text>
                          {renderSelect(getPermission(code), false, group.key)}
                        </Flex>
                      );
                    })}

                    {!group.noButton &&
                      (group.key === "orders" ? (
                        <Flex justify="space-between" align="center">
                          <Text>
                            {t(
                              "label.permissionsorders.removeparticipations",
                              "Remove all operator's existing participations from orders",
                            )}
                          </Text>
                          <Button
                            danger
                            style={{
                              marginTop: 16,
                              width: 180,
                              height: 32,
                              fontSize: 16,
                            }}
                            onClick={confirmRemoveOrderParticipations}
                          >
                            {t("buttons.remove")}
                          </Button>
                        </Flex>
                      ) : (
                        <Flex justify="space-between" align="center">
                          <Text>
                            {t("label.permissionsofseller.removeparticipations")}
                            {group?.title.toLowerCase()}
                          </Text>
                          <Button
                            danger
                            style={{
                              marginTop: 16,
                              width: 180,
                              height: 32,
                              fontSize: 16,
                            }}
                            onClick={() =>
                              handleRemoveGroup(
                                group.permissionCodes,
                                group.configCodes,
                              )
                            }
                          >
                            {t("buttons.remove")}
                          </Button>
                        </Flex>
                      ))}
                  </Card>
                </>
              ))}
            </Panel>
          </Collapse>
        </Flex>
        <Flex justify="space-between" style={{ marginTop: 24 }}>
          <Button
            danger
            icon={<ApiOutlined />}
            iconPosition="end"
            onClick={warningDelete}
          >
            {t("buttons.unlink")}
          </Button>
          <Flex gap={8}>
            <Button onClick={handleBack}>{t("buttons.cancel")}</Button>
            <Button
              type="primary"
              icon={<CheckOutlined />} iconPosition="end"
              onClick={handleSavePermissions}
            >
              {t("button.save")}
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Spin>
  );
};
